import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/loading-button';
import { cssMap, cx } from '@atlaskit/css';
import Form, { ErrorMessage, Field, RequiredAsterisk } from '@atlaskit/form';
import Link from '@atlaskit/link';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';
import { type User as UserPickerUser } from '@atlaskit/user-picker';
import type { UserPickerRef } from '@atlaskit/user-picker/types';
import { getAtlassianContextUrl } from '@atlassian/teams-common/context-config';
import { useTeamsPermissionsService } from '@atlassian/teams-common/use-teams-permissions';

import useTeamCreateDialogAnalytics from '../../hooks/use-team-create-dialog-analytics';
import { TeamMembershipSettings } from '../../types';
import type { InvitedUser } from '../../types/user';
import { MAX_MEMBERS, VOID_FUNC } from '../../utils/constants';
import { validateTeamName } from '../../utils/validators';
import { messages } from '../i18n';
import MemberPicker from '../MemberPicker';
import { useContainerPermissionsAnalyticsEvent } from '../TeamCreate/useContainerPermissionsAnalyticsEvent';
import TeamTypePicker from '../TeamTypePicker';

import NoAccessErrorState from './NoAccessErrorState';
import * as Styled from './styled';
import TeamNameInput from './TeamNameInput';
import TeamOnboardingImg from './TeamOnBoardingImg';
import { type TeamCreateDialogInternalProps } from './types';

const style = cssMap({
	requiredFieldSummary: {
		marginBottom: token('space.025'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
	},
});

const LEARN_MORE_LINK = `${getAtlassianContextUrl(
	'support',
)}/atlassian-account/docs/what-is-an-atlassian-team/`;

function TeamLearnMoreLink() {
	const { formatMessage } = useIntl();

	return fg('dst-a11y__replace-anchor-with-link__people-and-tea') ? (
		<Link target="_blank" rel="noreferrer" href={LEARN_MORE_LINK}>
			{formatMessage(messages.learnMore)}
		</Link>
	) : (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
		<a target="_blank" rel="noreferrer" href={LEARN_MORE_LINK}>
			{formatMessage(messages.learnMore)}
		</a>
	);
}

function TeamCreateDialog(props: TeamCreateDialogInternalProps) {
	const {
		proposedMembers = [],
		cloudId,
		maxSelectedMembers = MAX_MEMBERS,
		extraAnalyticsAttrs,
		enableMembershipSettingsChoice,
		initialMembershipSettings = enableMembershipSettingsChoice
			? TeamMembershipSettings.INVITE_ONLY
			: TeamMembershipSettings.OPEN,
		createTeamAndAddMembers = VOID_FUNC,
		setIsLoading = VOID_FUNC,
		onClose = VOID_FUNC,
		customMessages = {},
		imageProportion,
		descriptionProportion,
		customComponents = {},
		currentUser,
		orgId,
		enableTeamPermissionCheck,
		prepopulatedFields = {},
	} = props;
	const {
		modalTitle = null,
		descriptionContent = null,
		submitButtonLabel = null,
		imageContent = null,
	} = customMessages;
	const Header = customComponents.modalHeader ?? ModalHeader;
	const Title = customComponents.modalTitle ?? ModalTitle;
	const Body = customComponents.modalBody ?? ModalBody;
	const Footer = customComponents.modalFooter ?? ModalFooter;
	const {
		members: proposedMembersNext,
		membershipSettings: initialMembershipSettingsNext,
		teamName: initialTeamName,
	} = prepopulatedFields;

	const [teamName, setTeamName] = useState(initialTeamName || '');
	const [isFetching, setIsFetching] = useState(false);
	const [membershipSettings, setMembershipSettings] = useState(
		initialMembershipSettingsNext ? initialMembershipSettingsNext : initialMembershipSettings,
	);
	const [selectedMembers, setSelectedMembers] = useState<InvitedUser[]>([]);
	const [isSubmittedOnce, setIsSubmittedOnce] = useState(false);
	const [isMemberPickerInvalid, setIsMemberPickerInvalid] = useState(false);
	const { formatMessage } = useIntl();
	const { loading, permissions } = useTeamsPermissionsService(
		{ orgId, cloudId },
		{
			enabled: !!enableTeamPermissionCheck,
			attributes: {
				userId: currentUser?.id,
			},
		},
	);
	const userPickerRef = useRef<UserPickerRef>(null);
	const { fireEvent } = useAnalyticsEvents();
	const {
		triggerAnalyticsForSubmitTeamCreateDialog: triggerAnalyticsForSubmitTeamCreateDialogNext,
	} = useTeamCreateDialogAnalytics();

	useContainerPermissionsAnalyticsEvent(cloudId, currentUser?.id);

	const proposedMembersWithCurrentUser = useMemo(() => {
		const members = [
			...(proposedMembersNext ? proposedMembersNext : proposedMembers),
			...(currentUser && currentUser.id ? [currentUser] : []),
		];
		const uniqueMembers = members.filter(
			(member, index, self) => index === self.findIndex((m) => m.id === member.id),
		);
		return uniqueMembers;
	}, [proposedMembersNext, proposedMembers, currentUser]);

	// did mount
	useEffect(() => {
		setSelectedMembers(proposedMembersWithCurrentUser);

		fireEvent('screen.teamCreateDialog.viewed', {
			proposedMembersLength: (proposedMembersNext ? proposedMembersNext : proposedMembers).length,
			...extraAnalyticsAttrs,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setIsFetching(!!props.isFetching);
		setIsLoading(!!props.isFetching || loading);

		// when isFetching prop change from true --> false, that means the creating team finishes
		if (props.isFetching === false && isFetching === true) {
			onClose();
		}
	}, [props.isFetching, isFetching, setIsLoading, onClose, loading]);

	const handleSelectedChange = useCallback(
		(users: UserPickerUser[], data: { hasError: boolean; isDisabled: boolean }) => {
			setSelectedMembers(
				users.map((user) => ({
					id: user.id,
					email: user.email,
					fullName: user.name,
					avatarUrl: user.avatarUrl,
				})),
			);
			setIsMemberPickerInvalid(data.hasError || users.length === 0);
		},
		[setIsMemberPickerInvalid, setSelectedMembers],
	);

	const canTeamBeCreated = useCallback(() => {
		const teamNameValidationError = validateTeamName(teamName);
		return !isMemberPickerInvalid && !teamNameValidationError && !isFetching;
	}, [isFetching, isMemberPickerInvalid, teamName]);

	const handleSubmit = useCallback(() => {
		triggerAnalyticsForSubmitTeamCreateDialogNext({
			canCreateTeam: canTeamBeCreated(),
			numberOfMembers: selectedMembers.length,
			numberOfSuggestedMembers: 0, // As we don't have suggested members in this version
			...extraAnalyticsAttrs,
		});

		setIsSubmittedOnce(true);

		if (!canTeamBeCreated()) {
			const teamNameValidationError = validateTeamName(teamName);
			const inviteMembersValidationError = selectedMembers.length === 0;

			if (inviteMembersValidationError) {
				userPickerRef.current?.focus?.();
			}

			return {
				teamName: teamNameValidationError,
				inviteMembers: inviteMembersValidationError,
			};
		}

		if (canTeamBeCreated()) {
			setIsFetching(true);
			// outsource creating a new team + invite members logic to outside
			// to reduce complexity of current file
			createTeamAndAddMembers(teamName, selectedMembers, membershipSettings);
		}
	}, [
		canTeamBeCreated,
		createTeamAndAddMembers,
		extraAnalyticsAttrs,
		membershipSettings,
		selectedMembers,
		teamName,
		triggerAnalyticsForSubmitTeamCreateDialogNext,
	]);

	if (!permissions.canCreateTeams) {
		return (
			<Body>
				<NoAccessErrorState onAction={onClose} />
			</Body>
		);
	}

	return fg('platform-design_system_team-form_conversion') ? (
		<Form onSubmit={handleSubmit} noValidate>
			<Header>
				<Title>{modalTitle ?? formatMessage(messages.modalTitle)}</Title>
			</Header>
			<Body>
				<Styled.IllustratedDialogContent
					data-test-selector="create-team-content-dialog"
					data-testid="create-team-content-dialog"
				>
					<Styled.ImageSection imageProportion={imageProportion}>
						{imageContent ?? <TeamOnboardingImg />}
					</Styled.ImageSection>
					<Styled.TeamDataSection descriptionProportion={descriptionProportion}>
						{descriptionContent ?? (
							<Styled.DescriptionParagraph>
								{formatMessage(messages.formTextWithMention, {
									learnMoreLink: <TeamLearnMoreLink />,
								})}
							</Styled.DescriptionParagraph>
						)}
						<Box xcss={cx(style.requiredFieldSummary)}>
							{formatMessage(messages.requiredFieldSummary)}
							<RequiredAsterisk />
						</Box>
						<TeamNameInput
							onChange={setTeamName}
							onSubmit={handleSubmit}
							isSubmittedOnce={isSubmittedOnce}
							initialTeamName={teamName}
						/>
						<Styled.UserPickerWrapper>
							<Field
								name="inviteMembers"
								label={formatMessage(messages.invitationsFieldLabel)}
								id="inviteMembers"
								isRequired
							>
								{({ fieldProps, error }) => {
									// oxlint-disable-next-line no-unused-vars
									const { value, ...memberPickerProps } = fieldProps;

									const fieldIsInvalid = isSubmittedOnce ? isMemberPickerInvalid : !!error;

									return (
										<>
											<MemberPicker
												{...memberPickerProps}
												ref={userPickerRef}
												cloudId={cloudId}
												onChange={handleSelectedChange}
												initialValues={proposedMembersWithCurrentUser}
												maxSelectedMembers={maxSelectedMembers}
												extraAnalyticsAttrs={extraAnalyticsAttrs}
												showHelperMessages={!fieldIsInvalid}
												isInvalid={fieldIsInvalid}
												ariaLabelledBy={`${fieldProps.id}-label`}
												ariaDescribedBy={
													fieldIsInvalid ? `${fieldProps.id}-error` : `${fieldProps.id}-helper`
												}
												inputId={fieldProps.id}
											/>
											{fieldIsInvalid && (
												<ErrorMessage testId="invitation-field-empty-error">
													{formatMessage(messages.invitationsFieldErrorMessage, {
														maxSelectedMembers,
													})}
												</ErrorMessage>
											)}
										</>
									);
								}}
							</Field>
						</Styled.UserPickerWrapper>
						{enableMembershipSettingsChoice && (
							<TeamTypePicker
								onChange={setMembershipSettings}
								defaultValue={
									initialMembershipSettingsNext
										? initialMembershipSettingsNext
										: initialMembershipSettings
								}
							/>
						)}
					</Styled.TeamDataSection>
				</Styled.IllustratedDialogContent>
			</Body>
			<Footer>
				<Button appearance="subtle" onClick={onClose}>
					{formatMessage(messages.cancelButtonLabel)}
				</Button>
				<Button appearance="primary" isLoading={isFetching} type="submit">
					{submitButtonLabel ?? formatMessage(messages.submitButtonLabel)}
				</Button>
			</Footer>
		</Form>
	) : (
		<Form onSubmit={handleSubmit}>
			{({ formProps }) => (
				<form {...formProps} noValidate>
					<Header>
						<Title>{modalTitle ?? formatMessage(messages.modalTitle)}</Title>
					</Header>
					<Body>
						<Styled.IllustratedDialogContent
							data-test-selector="create-team-content-dialog"
							data-testid="create-team-content-dialog"
						>
							<Styled.ImageSection imageProportion={imageProportion}>
								{imageContent ?? <TeamOnboardingImg />}
							</Styled.ImageSection>
							<Styled.TeamDataSection descriptionProportion={descriptionProportion}>
								{descriptionContent ?? (
									<Styled.DescriptionParagraph>
										{formatMessage(messages.formTextWithMention, {
											learnMoreLink: <TeamLearnMoreLink />,
										})}
									</Styled.DescriptionParagraph>
								)}
								<Box xcss={cx(style.requiredFieldSummary)}>
									{formatMessage(messages.requiredFieldSummary)}
									<RequiredAsterisk />
								</Box>
								<TeamNameInput
									onChange={setTeamName}
									onSubmit={handleSubmit}
									isSubmittedOnce={isSubmittedOnce}
									initialTeamName={teamName}
								/>
								<Styled.UserPickerWrapper>
									<Field
										name="inviteMembers"
										label={formatMessage(messages.invitationsFieldLabel)}
										id="inviteMembers"
										isRequired
									>
										{({ fieldProps, error }) => {
											// oxlint-disable-next-line no-unused-vars
											const { value, ...memberPickerProps } = fieldProps;

											const fieldIsInvalid = isSubmittedOnce ? isMemberPickerInvalid : !!error;

											return (
												<>
													<MemberPicker
														{...memberPickerProps}
														ref={userPickerRef}
														cloudId={cloudId}
														onChange={handleSelectedChange}
														initialValues={proposedMembersWithCurrentUser}
														maxSelectedMembers={maxSelectedMembers}
														extraAnalyticsAttrs={extraAnalyticsAttrs}
														showHelperMessages={!fieldIsInvalid}
														isInvalid={fieldIsInvalid}
														ariaLabelledBy={`${fieldProps.id}-label`}
														ariaDescribedBy={
															fieldIsInvalid ? `${fieldProps.id}-error` : `${fieldProps.id}-helper`
														}
														inputId={fieldProps.id}
													/>
													{fieldIsInvalid && (
														<ErrorMessage testId="invitation-field-empty-error">
															{formatMessage(messages.invitationsFieldErrorMessage, {
																maxSelectedMembers,
															})}
														</ErrorMessage>
													)}
												</>
											);
										}}
									</Field>
								</Styled.UserPickerWrapper>
								{enableMembershipSettingsChoice && (
									<TeamTypePicker
										onChange={setMembershipSettings}
										defaultValue={
											initialMembershipSettingsNext
												? initialMembershipSettingsNext
												: initialMembershipSettings
										}
									/>
								)}
							</Styled.TeamDataSection>
						</Styled.IllustratedDialogContent>
					</Body>
					<Footer>
						<Button appearance="subtle" onClick={onClose}>
							{formatMessage(messages.cancelButtonLabel)}
						</Button>
						<Button appearance="primary" isLoading={isFetching} type="submit">
							{submitButtonLabel ?? formatMessage(messages.submitButtonLabel)}
						</Button>
					</Footer>
				</form>
			)}
		</Form>
	);
}

export default TeamCreateDialog;
